// src/pages/alianzas/SeguimientoAlianzasPage.js
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import esLocale from '@fullcalendar/core/locales/es';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle,
  Edit2,
  Loader2,
  MessageSquare,
  Plus,
  RefreshCw,
  Reply,
  Search,
  Send,
  Trash2,
  Users,
  ImageIcon,
  Paperclip,
  X,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { alianzasApi, apiErrorMessage } from '../../services/alianzasApi';

// ─── paleta Alianzas ──────────────────────────────────────────────────────
// Mismo azul que el equipo Alianzas ya tiene en el tablero de Timesheets
// (components/timesheets/HorasPorEquipo.jsx). Un teal se confundia con FEMSA.
const ACCENT = '#00B8FF';
const ACCENT_RGB = '0,184,255';
const tint = (alpha) => `rgba(${ACCENT_RGB},${alpha})`;
const BORDER = tint(0.25);
const PANEL_BG = 'linear-gradient(135deg,#04141f 0%,#071f30 50%,#04141f 100%)';

// El layout pinta un morado global; el area de Alianzas lo sustituye por un
// azul que combina con su acento sin salirse del branding.
const PAGE_BG =
  `radial-gradient(1100px 600px at 15% -10%, ${tint(0.12)} 0%, transparent 60%),` +
  'radial-gradient(900px 500px at 100% 0%, rgba(134,68,255,0.10) 0%, transparent 55%),' +
  'linear-gradient(135deg, #08203a 0%, #0b2b44 50%, #04121f 100%)';

const CARD =
  'rounded-2xl border border-sky-400/20 bg-[#061a2b]/60 backdrop-blur-sm shadow-lg shadow-black/20';

const BTN_PRIMARY = {
  background: tint(0.2),
  border: `1px solid ${BORDER}`,
  color: ACCENT,
};

const CLOUD_BADGE = {
  Azure:   { bg: 'rgba(56,189,248,0.18)',  color: '#38bdf8' },
  AWS:     { bg: 'rgba(251,146,60,0.18)',  color: '#fb923c' },
  GCP:     { bg: 'rgba(52,211,153,0.18)',  color: '#34d399' },
  OCI:     { bg: 'rgba(248,113,113,0.18)', color: '#f87171' },
  IBM:     { bg: 'rgba(129,140,248,0.18)', color: '#818cf8' },
  Huawei:  { bg: 'rgba(244,114,182,0.18)', color: '#f472b6' },
  Alibaba: { bg: 'rgba(250,204,21,0.18)',  color: '#facc15' },
  All:     { bg: 'rgba(148,163,184,0.18)', color: '#94a3b8' },
};

const STATUS_BADGE = {
  done:        { bg: 'rgba(74,222,128,0.18)',  color: '#4ade80', label: 'Completado' },
  in_progress: { bg: 'rgba(56,189,248,0.18)',  color: '#38bdf8', label: 'En curso' },
  on_hold:     { bg: 'rgba(148,163,184,0.18)', color: '#94a3b8', label: 'Pausado' },
  overdue:     { bg: 'rgba(248,113,113,0.18)', color: '#f87171', label: 'Vencido' },
  cancelled:   { bg: 'rgba(100,116,139,0.18)', color: '#64748b', label: 'Cancelado' },
};

const CLASIFICACION_BADGE = {
  capacitacion:      { bg: 'rgba(129,140,248,0.18)', color: '#818cf8', label: 'Capacitación' },
  sesion_fabricante: { bg: 'rgba(244,114,182,0.18)', color: '#f472b6', label: 'Sesión con Fabricante' },
  sesion_resultados: { bg: 'rgba(56,189,248,0.18)',  color: '#38bdf8', label: 'Sesión de resultados' },
  qbr:               { bg: 'rgba(250,204,21,0.18)',  color: '#facc15', label: 'QBR' },
  vacaciones:        { bg: 'rgba(248,113,113,0.18)', color: '#f87171', label: 'Vacaciones' },
};

const CLOUDS = ['Azure', 'AWS', 'GCP', 'OCI', 'IBM', 'Huawei', 'Alibaba', 'All'];
const ACTION_WITH = [
  'Alianzas', 'Comercial', 'Compras', 'Fabricante', 'Legal', 'Operaciones', 'Proveedor/Partner',
];
const STATUS_OPTIONS = Object.keys(STATUS_BADGE);
const CLASIFICACION_OPTIONS = Object.keys(CLASIFICACION_BADGE);

const PRIORIDAD_BADGE = {
  baja:  { bg: 'rgba(74,222,128,0.18)',  color: '#4ade80', label: 'Baja' },
  media: { bg: 'rgba(250,204,21,0.18)',  color: '#facc15', label: 'Media' },
  alta:  { bg: 'rgba(248,113,113,0.18)', color: '#f87171', label: 'Alta' },
};
const NIVELES_PRIORIDAD = Object.keys(PRIORIDAD_BADGE);

const PAGE_SIZE = 25;

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmtFecha = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtFechaHora = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const iniciales = (nombre = '') =>
  nombre.split(' ').slice(0, 2).map(p => p[0] || '').join('').toUpperCase();

// La API devuelve fecha_fin inclusiva; FullCalendar la espera exclusiva.
const diaSiguiente = (isoDate) => {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

function Badge({ value, map, fallbackLabel }) {
  if (!value) return <span className="text-white/25 text-xs">—</span>;
  const cfg = map[value] || { bg: 'rgba(100,116,139,0.2)', color: '#cbd5e1' };
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label || fallbackLabel || value}
    </span>
  );
}

function ProgressBar({ value = 0 }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ACCENT }} />
      </div>
      <span className="text-xs text-white/60 tabular-nums w-9 text-right">{pct}%</span>
    </div>
  );
}

function Avatar({ usuario, nombre, size = 24, style }) {
  const label = usuario?.nombre || nombre || '?';
  if (usuario?.avatar_url) {
    return (
      <img
        src={usuario.avatar_url}
        alt={label}
        title={label}
        // Google bloquea las fotos si se manda referer.
        referrerPolicy="no-referrer"
        className="rounded-full object-cover border border-[#04141f] shrink-0"
        style={{ width: size, height: size, ...style }}
      />
    );
  }
  return (
    <div
      title={label}
      className="rounded-full flex items-center justify-center font-bold text-white border border-[#04141f] shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        background: tint(0.35),
        ...style,
      }}
    >
      {iniciales(label)}
    </div>
  );
}

function Assignees({ items = [], catalogo = {} }) {
  if (!items.length) return <span className="text-white/25 text-xs">—</span>;
  const visibles = items.slice(0, 3);
  return (
    <div className="flex items-center">
      {visibles.map((a, i) => (
        <Avatar
          key={a.user_id || a.email || i}
          usuario={catalogo[a.user_id]}
          nombre={a.nombre || a.email}
          style={{ marginLeft: i ? -6 : 0 }}
        />
      ))}
      {items.length > 3 && (
        <span className="ml-2 text-xs text-white/50">+{items.length - 3}</span>
      )}
    </div>
  );
}

// Multi-select de nivel de prioridad: permite marcar baja/media/alta a la vez.
function NivelPrioridadFiltro({ value = [], onChange }) {
  const [open, setOpen] = useState(false);

  const toggle = (nivel) => onChange(
    value.includes(nivel) ? value.filter(v => v !== nivel) : [...value, nivel]
  );

  const label = value.length
    ? value.map(v => PRIORIDAD_BADGE[v].label).join(', ')
    : 'Todos los niveles';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`${selectCls} flex items-center gap-2 text-left`}
      >
        <span className="truncate max-w-[140px]">{label}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`${CARD} absolute left-0 mt-2 z-50 p-2 w-44`}>
            {NIVELES_PRIORIDAD.map(n => {
              const sel = value.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => toggle(n)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-left hover:bg-white/5 transition-colors"
                >
                  <span
                    className="w-4 h-4 rounded border flex items-center justify-center shrink-0"
                    style={{ borderColor: sel ? ACCENT : 'rgba(255,255,255,.25)', background: sel ? tint(0.3) : 'transparent' }}
                  >
                    {sel && <CheckCircle size={11} style={{ color: ACCENT }} />}
                  </span>
                  <Badge value={n} map={PRIORIDAD_BADGE} />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function AdjuntosComentario({ adjuntos = [] }) {
  if (!adjuntos.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {adjuntos.map((adj, i) => {
        if (adj.disponible === false) {
          return (
            <div
              key={i}
              title="La imagen venia de Notion con un enlace temporal que ya expiro"
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] text-white/35 border border-white/10"
            >
              <ImageIcon size={12} /> Adjunto no disponible
            </div>
          );
        }
        if (adj.categoria !== 'image') {
          return (
            <a
              key={i}
              href={adj.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] text-white/60 border border-white/10 hover:border-sky-400/50 hover:text-sky-300 transition-colors"
            >
              <Paperclip size={12} /> Ver documento adjunto
            </a>
          );
        }
        return (
          <a
            key={i}
            href={adj.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-lg overflow-hidden border border-white/10 hover:border-sky-400/50 transition-colors"
          >
            <img
              src={adj.url}
              alt="Adjunto del comentario"
              className="max-h-40 object-cover"
              loading="lazy"
            />
          </a>
        );
      })}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-sky-200/80">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  'rounded-lg px-3 py-2 text-sm bg-white/5 border border-sky-400/20 text-white placeholder-white/30 focus:outline-none focus:border-sky-300/60';
const selectCls =
  'rounded-lg px-3 py-2 text-sm bg-[#071f30] border border-sky-400/20 text-white focus:outline-none focus:border-sky-300/60';

function Toast({ msg, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3500);
    return () => clearTimeout(t);
  }, []);
  const isOk = type === 'ok';
  return (
    <div
      className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-2xl"
      style={{
        background: isOk ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)',
        border: `1px solid ${isOk ? 'rgba(74,222,128,0.4)' : 'rgba(239,68,68,0.4)'}`,
        color: isOk ? '#4ade80' : '#f87171',
      }}
    >
      {isOk ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
      {msg}
    </div>
  );
}

function ModalShell({ title, icon, onClose, children, wide }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className={`relative w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} rounded-2xl shadow-2xl overflow-hidden`}
        style={{ background: PANEL_BG, border: `1px solid ${BORDER}` }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-sky-400/15">
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-semibold text-white">{title}</span>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ConfirmDialog({ title, message, confirmLabel, busy, onClose, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: PANEL_BG, border: '1px solid rgba(239,68,68,0.3)' }}
      >
        <div className="flex items-center gap-2 mb-3 text-red-400">
          <AlertCircle size={18} />
          <span className="font-semibold">{title}</span>
        </div>
        <p className="text-sm text-white/70 mb-4">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-white/15 text-white/60 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 transition-all disabled:opacity-50"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de asignacion ─────────────────────────────────────────────────────

const EMPTY_ASIGNACION = {
  nombre: '',
  cloud: 'Azure',
  status_norm: 'in_progress',
  progreso_pct: 0,
  action_with: '',
  assignees: [],
  es_prioridad: false,
  nivel_prioridad: '',
};

function AsignacionModal({ mode, asignacion, usuarios, onClose, onSave }) {
  const [form, setForm] = useState(() =>
    mode === 'edit' && asignacion
      ? {
          nombre: asignacion.nombre || '',
          cloud: asignacion.cloud || '',
          status_norm: asignacion.status_norm || 'in_progress',
          progreso_pct: Number(asignacion.progreso_pct) || 0,
          action_with: asignacion.action_with || '',
          assignees: asignacion.assignees || [],
          es_prioridad: Boolean(asignacion.es_prioridad),
          nivel_prioridad: asignacion.nivel_prioridad || '',
        }
      : { ...EMPTY_ASIGNACION }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const change = (name, value) => setForm(f => ({ ...f, [name]: value }));

  const toggleAssignee = (usuario) => {
    setForm(f => {
      const exists = f.assignees.some(a => a.user_id === usuario.user_id);
      return {
        ...f,
        assignees: exists
          ? f.assignees.filter(a => a.user_id !== usuario.user_id)
          : [...f.assignees, {
              user_id: usuario.user_id,
              nombre: usuario.nombre,
              email: usuario.email || '',
            }],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ ...form, nombre: form.nombre.trim(), progreso_pct: Number(form.progreso_pct) });
    } catch (err) {
      setError(apiErrorMessage(err, 'Error al guardar la asignación'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={mode === 'create' ? 'Nueva Asignación' : 'Editar Asignación'}
      icon={<Users size={18} style={{ color: ACCENT }} />}
      onClose={onClose}
    >
      <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
        <div className="col-span-2">
          <Field label="Nombre" required>
            <input
              className={inputCls}
              value={form.nombre}
              onChange={e => change('nombre', e.target.value)}
              placeholder="Ej. Azure Accelerate: Assessment Odessa"
            />
          </Field>
        </div>

        <Field label="Cloud">
          <select className={selectCls} value={form.cloud} onChange={e => change('cloud', e.target.value)}>
            <option value="">Sin definir</option>
            {CLOUDS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>

        <Field label="Status" required>
          <select
            className={selectCls}
            value={form.status_norm}
            onChange={e => change('status_norm', e.target.value)}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{STATUS_BADGE[s].label}</option>
            ))}
          </select>
        </Field>

        <Field label="Action With">
          <select
            className={selectCls}
            value={form.action_with}
            onChange={e => change('action_with', e.target.value)}
          >
            <option value="">Sin definir</option>
            {ACTION_WITH.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>

        <Field label={`Progreso (${form.progreso_pct}%)`}>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={form.progreso_pct}
            onChange={e => change('progreso_pct', e.target.value)}
            className="accent-sky-400"
          />
        </Field>

        <Field label="Prioridad">
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer select-none">
            <input
              type="checkbox"
              className="accent-sky-400 w-4 h-4"
              checked={form.es_prioridad}
              onChange={e => change('es_prioridad', e.target.checked)}
            />
            Es prioridad
          </label>
        </Field>

        <Field label="Nivel de prioridad">
          <select
            className={selectCls}
            value={form.nivel_prioridad}
            onChange={e => change('nivel_prioridad', e.target.value)}
            disabled={!form.es_prioridad}
          >
            <option value="">Sin definir</option>
            {NIVELES_PRIORIDAD.map(n => (
              <option key={n} value={n}>{PRIORIDAD_BADGE[n].label}</option>
            ))}
          </select>
        </Field>

        <div className="col-span-2">
          <Field label="Assignees">
            <div className="flex flex-wrap gap-2 rounded-lg p-3 bg-white/5 border border-sky-400/15">
              {usuarios.length === 0 && (
                <span className="text-xs text-white/40">No hay usuarios disponibles</span>
              )}
              {usuarios.map(u => {
                const active = form.assignees.some(a => a.user_id === u.user_id);
                return (
                  <button
                    type="button"
                    key={u.user_id}
                    onClick={() => toggleAssignee(u)}
                    className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
                    style={{
                      background: active ? tint(0.22) : 'rgba(255,255,255,0.05)',
                      color: active ? ACCENT : 'rgba(255,255,255,0.6)',
                      border: `1px solid ${active ? BORDER : 'rgba(255,255,255,0.1)'}`,
                    }}
                  >
                    {u.nombre || u.email}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        {error && (
          <div className="col-span-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="col-span-2 flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-white/15 text-white/60 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
            style={BTN_PRIMARY}
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Panel de comentarios ────────────────────────────────────────────────────

function ComentariosPanel({ item, canWrite, catalogo = {}, onClose, onChanged, showToast }) {
  const [hilos, setHilos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevo, setNuevo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editando, setEditando] = useState(null); // { comentario_id, texto }
  const [borrando, setBorrando] = useState(null);
  const [borrandoBusy, setBorrandoBusy] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]); // [{ url, preview }]
  const [subiendo, setSubiendo] = useState(false);
  const fileInputRef = useRef(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await alianzasApi.listComentarios(item.origen, item.id);
      setHilos(data.hilos || []);
    } catch (err) {
      showToast(apiErrorMessage(err, 'Error cargando comentarios'), 'error');
    } finally {
      setLoading(false);
    }
  }, [item.origen, item.id]);

  useEffect(() => { cargar(); }, [cargar]);

  const agregarArchivos = async (files) => {
    const imagenes = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    if (!imagenes.length) return;

    setSubiendo(true);
    try {
      for (const file of imagenes) {
        const res = await alianzasApi.subirAdjunto(file);
        setAdjuntos(prev => [
          ...prev,
          { url: res.url, preview: URL.createObjectURL(file) },
        ]);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, 'No se pudo subir la imagen'), 'error');
    } finally {
      setSubiendo(false);
    }
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      agregarArchivos(files);
    }
  };

  const quitarAdjunto = (idx) => {
    setAdjuntos(prev => {
      const adj = prev[idx];
      if (adj?.preview) URL.revokeObjectURL(adj.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const enviar = async () => {
    const texto = nuevo.trim();
    if (!texto && !adjuntos.length) return;
    setEnviando(true);
    try {
      await alianzasApi.createComentario(item.origen, item.id, {
        texto,
        discussion_id: replyTo || undefined,
        adjuntos: adjuntos.map(a => ({ categoria: 'image', url: a.url })),
      });
      adjuntos.forEach(a => a.preview && URL.revokeObjectURL(a.preview));
      setAdjuntos([]);
      setNuevo('');
      setReplyTo(null);
      await cargar();
      onChanged();
    } catch (err) {
      showToast(apiErrorMessage(err, 'No se pudo agregar el comentario'), 'error');
    } finally {
      setEnviando(false);
    }
  };

  const guardarEdicion = async () => {
    const texto = (editando?.texto || '').trim();
    if (!texto) return;
    try {
      await alianzasApi.updateComentario(editando.comentario_id, { texto });
      setEditando(null);
      await cargar();
      showToast('Comentario actualizado');
    } catch (err) {
      showToast(apiErrorMessage(err, 'No se pudo editar el comentario'), 'error');
    }
  };

  const confirmarBorrado = async () => {
    setBorrandoBusy(true);
    try {
      await alianzasApi.deleteComentario(borrando.comentario_id);
      setBorrando(null);
      await cargar();
      onChanged();
      showToast('Comentario eliminado');
    } catch (err) {
      showToast(apiErrorMessage(err, 'No se pudo eliminar el comentario'), 'error');
    } finally {
      setBorrandoBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} />
      <aside
        className="fixed top-0 right-0 bottom-0 z-40 w-full max-w-xl flex flex-col"
        style={{ background: PANEL_BG, borderLeft: `1px solid ${BORDER}` }}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-sky-400/15">
          <div>
            <div className="flex items-center gap-2 text-white font-semibold">
              <MessageSquare size={17} style={{ color: ACCENT }} />
              Comentarios
            </div>
            <p className="text-xs text-white/50 mt-1">{item.nombre}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading && (
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <Loader2 size={14} className="animate-spin" /> Cargando…
            </div>
          )}

          {!loading && hilos.length === 0 && (
            <p className="text-sm text-white/40">Aún no hay comentarios en esta asignación.</p>
          )}

          {!loading && hilos.map(hilo => (
            <div
              key={hilo.discussion_id}
              className="rounded-xl p-4 space-y-4"
              style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${tint(0.12)}` }}
            >
              {hilo.nivel === 'inline' && (
                <span className="text-[10px] uppercase tracking-wide text-sky-300/60">
                  Comentario en bloque
                </span>
              )}

              {hilo.mensajes.map(msg => {
                const autor = catalogo[msg.autor_id];
                return (
                <div key={msg.comentario_id} className="flex gap-3">
                  <Avatar usuario={autor} nombre={msg.autor_nombre || msg.autor_email} size={28} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white">
                        {autor?.nombre || msg.autor_nombre || msg.autor_email || 'Anónimo'}
                      </span>
                      <span className="text-[11px] text-white/40">{fmtFechaHora(msg.creado_en)}</span>
                      {msg.editado_en && <span className="text-[11px] text-white/30">(editado)</span>}
                    </div>

                    {editando?.comentario_id === msg.comentario_id ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          className={`${inputCls} w-full resize-none`}
                          rows={3}
                          value={editando.texto}
                          onChange={e => setEditando(s => ({ ...s, texto: e.target.value }))}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={guardarEdicion}
                            className="px-3 py-1 rounded-lg text-xs font-semibold"
                            style={BTN_PRIMARY}
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => setEditando(null)}
                            className="px-3 py-1 rounded-lg text-xs border border-white/15 text-white/60"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-white/75 whitespace-pre-wrap break-words">
                        {msg.texto_markdown || msg.texto}
                      </p>
                    )}

                    <AdjuntosComentario adjuntos={msg.adjuntos} />

                    {msg.es_propio && !editando && (
                      <div className="flex gap-3 mt-2">
                        <button
                          onClick={() => setEditando({ comentario_id: msg.comentario_id, texto: msg.texto || '' })}
                          className="flex items-center gap-1 text-[11px] text-white/45 hover:text-sky-300 transition-colors"
                        >
                          <Edit2 size={11} /> Editar
                        </button>
                        <button
                          onClick={() => setBorrando(msg)}
                          className="flex items-center gap-1 text-[11px] text-white/45 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={11} /> Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}

              {canWrite && (
                <button
                  onClick={() => setReplyTo(hilo.discussion_id)}
                  className="flex items-center gap-1 text-[11px] text-sky-300/70 hover:text-sky-300 transition-colors"
                >
                  <Reply size={11} /> Responder en este hilo
                </button>
              )}
            </div>
          ))}
        </div>

        {canWrite ? (
          <div className="px-6 py-4 border-t border-sky-400/15 space-y-2">
            {replyTo && (
              <div className="flex items-center justify-between text-[11px] text-sky-300/80">
                <span>Respondiendo a un hilo existente</span>
                <button onClick={() => setReplyTo(null)} className="text-white/40 hover:text-white">
                  Cancelar
                </button>
              </div>
            )}
            {adjuntos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {adjuntos.map((adj, i) => (
                  <div key={adj.url} className="relative">
                    <img
                      src={adj.preview}
                      alt="Adjunto por enviar"
                      className="h-16 w-16 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      onClick={() => quitarAdjunto(i)}
                      className="absolute -top-1.5 -right-1.5 rounded-full bg-red-500/90 text-white p-0.5"
                      title="Quitar"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-end">
              <textarea
                className={`${inputCls} flex-1 resize-none`}
                rows={2}
                placeholder="Escribe un comentario o pega una imagen…"
                value={nuevo}
                onChange={e => setNuevo(e.target.value)}
                onPaste={handlePaste}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                multiple
                className="hidden"
                onChange={e => { agregarArchivos(e.target.files); e.target.value = ''; }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={subiendo}
                title="Adjuntar imagen"
                className="p-2 rounded-lg border border-white/15 text-white/50 hover:text-white transition-colors disabled:opacity-40"
              >
                {subiendo ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
              </button>
              <button
                onClick={enviar}
                disabled={enviando || subiendo || (!nuevo.trim() && !adjuntos.length)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40"
                style={BTN_PRIMARY}
              >
                {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-4 border-t border-sky-400/15 text-xs text-white/40">
            No tienes permisos para comentar.
          </div>
        )}
      </aside>

      {borrando && (
        <ConfirmDialog
          title="Eliminar comentario"
          message="¿Confirmas eliminar tu comentario? Esta acción no se puede deshacer."
          confirmLabel={borrandoBusy ? 'Eliminando…' : 'Eliminar'}
          busy={borrandoBusy}
          onClose={() => setBorrando(null)}
          onConfirm={confirmarBorrado}
        />
      )}
    </>
  );
}

// ─── Modal de sesion ─────────────────────────────────────────────────────────

const hoyISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_SESION = {
  nombre: '',
  cloud: 'Azure',
  clasificacion_norm: 'capacitacion',
  fecha_inicio: hoyISO(),
  fecha_fin: '',
  es_dia_completo: true,
  inicio_ts: '',
  fin_ts: '',
  duracion_horas: '',
  zona_horaria: 'America/Mexico_City',
};

function SesionModal({ mode, sesion, onClose, onSave }) {
  const [form, setForm] = useState(() =>
    mode === 'edit' && sesion
      ? {
          nombre: sesion.nombre || '',
          cloud: sesion.cloud || '',
          clasificacion_norm: sesion.clasificacion_norm || 'capacitacion',
          fecha_inicio: (sesion.fecha_inicio || '').slice(0, 10) || hoyISO(),
          fecha_fin: (sesion.fecha_fin || '').slice(0, 10),
          es_dia_completo: sesion.es_dia_completo !== false,
          inicio_ts: sesion.inicio_ts ? sesion.inicio_ts.slice(0, 16) : '',
          fin_ts: sesion.fin_ts ? sesion.fin_ts.slice(0, 16) : '',
          duracion_horas: sesion.duracion_horas ?? '',
          zona_horaria: sesion.zona_horaria || 'America/Mexico_City',
        }
      : { ...EMPTY_SESION }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const change = (name, value) => setForm(f => ({ ...f, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    if (!form.fecha_inicio) {
      setError('La fecha de inicio es obligatoria.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        nombre: form.nombre.trim(),
        cloud: form.cloud || null,
        clasificacion_norm: form.clasificacion_norm,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.fecha_fin || form.fecha_inicio,
        es_dia_completo: form.es_dia_completo,
        inicio_ts: !form.es_dia_completo && form.inicio_ts ? new Date(form.inicio_ts).toISOString() : null,
        fin_ts: !form.es_dia_completo && form.fin_ts ? new Date(form.fin_ts).toISOString() : null,
        duracion_horas:
          !form.es_dia_completo && form.duracion_horas !== '' ? Number(form.duracion_horas) : null,
        zona_horaria: form.zona_horaria,
      });
    } catch (err) {
      setError(apiErrorMessage(err, 'Error al guardar la sesión'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={mode === 'create' ? 'Nueva Sesión / Capacitación' : 'Editar Sesión / Capacitación'}
      icon={<CalendarDays size={18} style={{ color: ACCENT }} />}
      onClose={onClose}
    >
      <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
        <div className="col-span-2">
          <Field label="Nombre" required>
            <input
              className={inputCls}
              value={form.nombre}
              onChange={e => change('nombre', e.target.value)}
              placeholder="Ej. Sesión 1 Economic Value Selling"
            />
          </Field>
        </div>

        <Field label="Cloud">
          <select className={selectCls} value={form.cloud} onChange={e => change('cloud', e.target.value)}>
            <option value="">Sin definir</option>
            {CLOUDS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>

        <Field label="Clasificación" required>
          <select
            className={selectCls}
            value={form.clasificacion_norm}
            onChange={e => change('clasificacion_norm', e.target.value)}
          >
            {CLASIFICACION_OPTIONS.map(c => (
              <option key={c} value={c}>{CLASIFICACION_BADGE[c].label}</option>
            ))}
          </select>
        </Field>

        <Field label="Fecha inicio" required>
          <input
            type="date"
            className={inputCls}
            value={form.fecha_inicio}
            onChange={e => change('fecha_inicio', e.target.value)}
          />
        </Field>

        <Field label="Fecha fin">
          <input
            type="date"
            className={inputCls}
            value={form.fecha_fin}
            onChange={e => change('fecha_fin', e.target.value)}
          />
        </Field>

        <div className="col-span-2 flex items-center gap-2">
          <input
            id="dia-completo"
            type="checkbox"
            className="accent-sky-400"
            checked={form.es_dia_completo}
            onChange={e => change('es_dia_completo', e.target.checked)}
          />
          <label htmlFor="dia-completo" className="text-sm text-white/70">
            Evento de día completo
          </label>
        </div>

        {!form.es_dia_completo && (
          <>
            <Field label="Inicio">
              <input
                type="datetime-local"
                className={inputCls}
                value={form.inicio_ts}
                onChange={e => change('inicio_ts', e.target.value)}
              />
            </Field>
            <Field label="Fin">
              <input
                type="datetime-local"
                className={inputCls}
                value={form.fin_ts}
                onChange={e => change('fin_ts', e.target.value)}
              />
            </Field>
            <Field label="Duración (horas)">
              <input
                type="number"
                min="0"
                step="0.25"
                className={inputCls}
                value={form.duracion_horas}
                onChange={e => change('duracion_horas', e.target.value)}
              />
            </Field>
          </>
        )}

        {error && (
          <div className="col-span-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="col-span-2 flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-white/15 text-white/60 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
            style={BTN_PRIMARY}
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Pagina principal ────────────────────────────────────────────────────────

export default function SeguimientoAlianzasPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('alianzas.seguimiento.create');
  const canEdit = hasPermission('alianzas.seguimiento.edit');
  const canDelete = hasPermission('alianzas.seguimiento.delete');
  const canComment = hasPermission('alianzas.comentarios.write');

  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type = 'ok') => setToast({ msg, type }), []);

  const [usuarios, setUsuarios] = useState([]);

  // El array assignees viene desnormalizado desde Notion (nombre viejo, sin
  // foto); el catalogo de usuarios es la fuente de verdad para mostrarlos.
  const usuariosPorId = useMemo(
    () => Object.fromEntries(usuarios.map(u => [u.user_id, u])),
    [usuarios]
  );

  // ── asignaciones ──────────────────────────────────────────────────────────
  const [asignaciones, setAsignaciones] = useState([]);
  const [totalAsignaciones, setTotalAsignaciones] = useState(0);
  const [loadingAsignaciones, setLoadingAsignaciones] = useState(true);
  const [filtrosAsig, setFiltrosAsig] = useState({ cloud: '', status_norm: '', action_with: '', assignee_id: '', es_prioridad: '', nivel_prioridad: [], search: '' });
  const [offsetAsig, setOffsetAsig] = useState(0);
  const [modalAsig, setModalAsig] = useState(null);
  const [borrarAsig, setBorrarAsig] = useState(null);
  const [borrandoAsig, setBorrandoAsig] = useState(false);
  const [comentariosDe, setComentariosDe] = useState(null);

  // ── sesiones ──────────────────────────────────────────────────────────────
  const [sesiones, setSesiones] = useState([]);
  const [loadingSesiones, setLoadingSesiones] = useState(true);
  const [filtrosSes, setFiltrosSes] = useState({ cloud: '', clasificacion_norm: '', search: '' });
  const [offsetSesiones, setOffsetSesiones] = useState(0);
  const [modalSes, setModalSes] = useState(null);
  const [borrarSes, setBorrarSes] = useState(null);
  const [borrandoSes, setBorrandoSes] = useState(false);

  // ── calendario ────────────────────────────────────────────────────────────
  const [rangoCal, setRangoCal] = useState(null);
  const [eventos, setEventos] = useState([]);

  useEffect(() => {
    alianzasApi.getUsuarios()
      .then(res => setUsuarios(res.data || []))
      .catch(() => setUsuarios([]));
  }, []);

  const cargarAsignaciones = useCallback(async () => {
    setLoadingAsignaciones(true);
    try {
      const params = { limit: PAGE_SIZE, offset: offsetAsig };
      if (filtrosAsig.cloud) params.cloud = [filtrosAsig.cloud];
      if (filtrosAsig.status_norm) params.status_norm = [filtrosAsig.status_norm];
      if (filtrosAsig.action_with) params.action_with = [filtrosAsig.action_with];
      if (filtrosAsig.assignee_id) params.assignee_id = filtrosAsig.assignee_id;
      if (filtrosAsig.es_prioridad) params.es_prioridad = filtrosAsig.es_prioridad === 'si';
      if (filtrosAsig.nivel_prioridad.length) params.nivel_prioridad = filtrosAsig.nivel_prioridad;
      if (filtrosAsig.search) params.search = filtrosAsig.search;

      const res = await alianzasApi.listAsignaciones(params);
      setAsignaciones(res.data || []);
      setTotalAsignaciones(res.total || 0);
    } catch (err) {
      showToast(apiErrorMessage(err, 'Error cargando asignaciones'), 'error');
    } finally {
      setLoadingAsignaciones(false);
    }
  }, [filtrosAsig, offsetAsig]);

  const cargarSesiones = useCallback(async () => {
    setLoadingSesiones(true);
    try {
      const params = { limit: 200, offset: 0 };
      if (filtrosSes.cloud) params.cloud = [filtrosSes.cloud];
      if (filtrosSes.clasificacion_norm) params.clasificacion_norm = [filtrosSes.clasificacion_norm];
      if (filtrosSes.search) params.search = filtrosSes.search;

      const res = await alianzasApi.listSesiones(params);
      setSesiones(res.data || []);
    } catch (err) {
      showToast(apiErrorMessage(err, 'Error cargando sesiones'), 'error');
    } finally {
      setLoadingSesiones(false);
    }
  }, [filtrosSes]);

  const cargarCalendario = useCallback(async () => {
    if (!rangoCal) return;
    try {
      const res = await alianzasApi.getCalendario(rangoCal);
      const items = (res.data || []).map(s => {
        const cfg = CLASIFICACION_BADGE[s.clasificacion_norm] || {};
        const base = {
          id: s.sesion_id,
          title: s.nombre,
          backgroundColor: cfg.bg || tint(0.18),
          borderColor: cfg.color || ACCENT,
          textColor: cfg.color || '#fff',
          extendedProps: { clasificacion: s.clasificacion_norm, cloud: s.cloud },
        };
        if (s.es_dia_completo !== false || !s.inicio_ts) {
          const inicio = (s.fecha_inicio || '').slice(0, 10);
          const fin = (s.fecha_fin || s.fecha_inicio || '').slice(0, 10);
          return { ...base, allDay: true, start: inicio, end: diaSiguiente(fin) };
        }
        return { ...base, start: s.inicio_ts, end: s.fin_ts || s.inicio_ts };
      });
      setEventos(items);
    } catch (err) {
      showToast(apiErrorMessage(err, 'Error cargando el calendario'), 'error');
    }
  }, [rangoCal]);

  useEffect(() => { cargarAsignaciones(); }, [cargarAsignaciones]);
  useEffect(() => { cargarSesiones(); }, [cargarSesiones]);
  useEffect(() => { cargarCalendario(); }, [cargarCalendario]);

  // ── acciones asignaciones ─────────────────────────────────────────────────
  const guardarAsignacion = async (form) => {
    if (modalAsig.mode === 'edit') {
      await alianzasApi.updateAsignacion(modalAsig.asignacion.asignacion_id, form);
      showToast('Asignación actualizada');
    } else {
      await alianzasApi.createAsignacion(form);
      showToast('Asignación creada');
    }
    setModalAsig(null);
    setOffsetAsig(0);
    cargarAsignaciones();
  };

  const eliminarAsignacion = async () => {
    setBorrandoAsig(true);
    try {
      await alianzasApi.deleteAsignacion(borrarAsig.asignacion_id);
      setBorrarAsig(null);
      showToast('Asignación enviada a papelera');
      cargarAsignaciones();
    } catch (err) {
      showToast(apiErrorMessage(err, 'No se pudo eliminar'), 'error');
    } finally {
      setBorrandoAsig(false);
    }
  };

  // ── acciones sesiones ─────────────────────────────────────────────────────
  const guardarSesion = async (form) => {
    if (modalSes.mode === 'edit') {
      await alianzasApi.updateSesion(modalSes.sesion.sesion_id, form);
      showToast('Sesión actualizada');
    } else {
      await alianzasApi.createSesion(form);
      showToast('Sesión creada');
    }
    setModalSes(null);
    setOffsetSesiones(0);
    cargarSesiones();
    cargarCalendario();
  };

  const eliminarSesion = async () => {
    setBorrandoSes(true);
    try {
      await alianzasApi.deleteSesion(borrarSes.sesion_id);
      setBorrarSes(null);
      showToast('Sesión enviada a papelera');
      setOffsetSesiones(0);
      cargarSesiones();
      cargarCalendario();
    } catch (err) {
      showToast(apiErrorMessage(err, 'No se pudo eliminar'), 'error');
    } finally {
      setBorrandoSes(false);
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(totalAsignaciones / PAGE_SIZE));
  const paginaActual = Math.floor(offsetAsig / PAGE_SIZE) + 1;
  const totalPaginasSesiones = Math.max(1, Math.ceil(sesiones.length / PAGE_SIZE));
  const paginaSesiones = Math.floor(offsetSesiones / PAGE_SIZE) + 1;
  const sesionesPagina = sesiones.slice(offsetSesiones, offsetSesiones + PAGE_SIZE);

  const kpis = useMemo(() => {
    const activas = asignaciones.filter(a => a.status_norm === 'in_progress').length;
    const vencidas = asignaciones.filter(a => a.status_norm === 'overdue').length;
    return { activas, vencidas };
  }, [asignaciones]);

  return (
    <div
      className="p-6 space-y-8 min-h-screen"
      style={{ background: PAGE_BG, backgroundAttachment: 'fixed' }}
    >
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users size={22} style={{ color: ACCENT }} />
            Seguimiento Alianzas
          </h1>
          <p className="text-sm text-white/50 mt-1">
            Asignaciones, comentarios y agenda de sesiones del equipo de Alianzas.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-white/50">
          <span>{totalAsignaciones} asignaciones</span>
          <span>·</span>
          <span>{kpis.activas} en curso</span>
          {kpis.vencidas > 0 && (
            <>
              <span>·</span>
              <span className="text-red-400">{kpis.vencidas} vencidas</span>
            </>
          )}
        </div>
      </header>

      {/* ── Asignaciones ─────────────────────────────────────────────────── */}
      <section className={`${CARD} p-5`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-white">Asignaciones</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                className={`${inputCls} pl-8 w-56`}
                placeholder="Buscar por nombre…"
                value={filtrosAsig.search}
                onChange={e => { setFiltrosAsig(f => ({ ...f, search: e.target.value })); setOffsetAsig(0); }}
              />
            </div>
            <select
              className={selectCls}
              value={filtrosAsig.cloud}
              onChange={e => { setFiltrosAsig(f => ({ ...f, cloud: e.target.value })); setOffsetAsig(0); }}
            >
              <option value="">Todas las nubes</option>
              {CLOUDS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              className={selectCls}
              value={filtrosAsig.status_norm}
              onChange={e => { setFiltrosAsig(f => ({ ...f, status_norm: e.target.value })); setOffsetAsig(0); }}
            >
              <option value="">Todos los status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_BADGE[s].label}</option>)}
            </select>
            <select
              className={selectCls}
              value={filtrosAsig.action_with}
              onChange={e => { setFiltrosAsig(f => ({ ...f, action_with: e.target.value })); setOffsetAsig(0); }}
            >
              <option value="">Todos los Action With</option>
              {ACTION_WITH.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select
              className={selectCls}
              value={filtrosAsig.assignee_id}
              onChange={e => { setFiltrosAsig(f => ({ ...f, assignee_id: e.target.value })); setOffsetAsig(0); }}
            >
              <option value="">Todos los assignees</option>
              {usuarios.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.nombre || u.email}</option>
              ))}
            </select>
            <select
              className={selectCls}
              value={filtrosAsig.es_prioridad}
              onChange={e => { setFiltrosAsig(f => ({ ...f, es_prioridad: e.target.value })); setOffsetAsig(0); }}
            >
              <option value="">Prioridad: todas</option>
              <option value="si">Prioridad: Sí</option>
              <option value="no">Prioridad: No</option>
            </select>
            <NivelPrioridadFiltro
              value={filtrosAsig.nivel_prioridad}
              onChange={niveles => { setFiltrosAsig(f => ({ ...f, nivel_prioridad: niveles })); setOffsetAsig(0); }}
            />
            <button
              onClick={cargarAsignaciones}
              className="p-2 rounded-lg border border-white/10 text-white/50 hover:text-white transition-colors"
              title="Refrescar"
            >
              <RefreshCw size={14} className={loadingAsignaciones ? 'animate-spin' : ''} />
            </button>
            {canCreate && (
              <button
                onClick={() => setModalAsig({ mode: 'create' })}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
                style={BTN_PRIMARY}
              >
                <Plus size={14} /> Nueva
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-white/40 border-b border-white/10">
                <th className="py-2 pr-3 font-medium">Nombre</th>
                <th className="py-2 px-3 font-medium">Cloud</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium">Progreso</th>
                <th className="py-2 px-3 font-medium">Prioridad</th>
                <th className="py-2 px-3 font-medium">Action With</th>
                <th className="py-2 px-3 font-medium">Assignee</th>
                <th className="py-2 px-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loadingAsignaciones && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-white/40">
                    <Loader2 size={18} className="animate-spin inline mr-2" /> Cargando asignaciones…
                  </td>
                </tr>
              )}

              {!loadingAsignaciones && asignaciones.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-white/40">
                    No hay asignaciones con los filtros seleccionados.
                  </td>
                </tr>
              )}

              {!loadingAsignaciones && asignaciones.map(a => (
                <tr key={a.asignacion_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{a.nombre}</span>
                      <button
                        onClick={() => setComentariosDe({ id: a.asignacion_id, nombre: a.nombre, origen: 'asignacion' })}
                        className="flex items-center gap-1 text-[11px] text-white/40 hover:text-sky-300 transition-colors"
                        title="Ver comentarios"
                      >
                        <MessageSquare size={12} />
                        {a.num_comentarios ?? 0}
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-3"><Badge value={a.cloud} map={CLOUD_BADGE} /></td>
                  <td className="py-3 px-3"><Badge value={a.status_norm} map={STATUS_BADGE} /></td>
                  <td className="py-3 px-3"><ProgressBar value={a.progreso_pct} /></td>
                  <td className="py-3 px-3">
                    {a.es_prioridad
                      ? (a.nivel_prioridad
                        ? <Badge value={a.nivel_prioridad} map={PRIORIDAD_BADGE} />
                        : <span className="text-xs text-white/50">Sí</span>)
                      : <span className="text-white/25 text-xs">—</span>}
                  </td>
                  <td className="py-3 px-3 text-white/60 text-xs">{a.action_with || '—'}</td>
                  <td className="py-3 px-3"><Assignees items={a.assignees} catalogo={usuariosPorId} /></td>
                  <td className="py-3 px-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setComentariosDe({ id: a.asignacion_id, nombre: a.nombre, origen: 'asignacion' })}
                        className="p-1.5 rounded-lg text-white/40 hover:text-sky-300 transition-colors"
                        title="Comentarios"
                      >
                        <MessageSquare size={14} />
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => setModalAsig({ mode: 'edit', asignacion: a })}
                          className="p-1.5 rounded-lg text-white/40 hover:text-sky-300 transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => setBorrarAsig(a)}
                          className="p-1.5 rounded-lg text-white/40 hover:text-red-400 transition-colors"
                          title="Enviar a papelera"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="flex items-center justify-between mt-4 text-xs text-white/50">
            <span>Página {paginaActual} de {totalPaginas}</span>
            <div className="flex gap-2">
              <button
                disabled={offsetAsig === 0}
                onClick={() => setOffsetAsig(o => Math.max(0, o - PAGE_SIZE))}
                className="px-3 py-1.5 rounded-lg border border-white/10 disabled:opacity-30 hover:text-white transition-colors"
              >
                Anterior
              </button>
              <button
                disabled={paginaActual >= totalPaginas}
                onClick={() => setOffsetAsig(o => o + PAGE_SIZE)}
                className="px-3 py-1.5 rounded-lg border border-white/10 disabled:opacity-30 hover:text-white transition-colors"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Sesiones / Capacitaciones ────────────────────────────────────── */}
      <section className={`${CARD} p-5`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-white">Sesiones / Capacitaciones</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                className={`${inputCls} pl-8 w-56`}
                placeholder="Buscar por nombre…"
                value={filtrosSes.search}
                onChange={e => {
                  setOffsetSesiones(0);
                  setFiltrosSes(f => ({ ...f, search: e.target.value }));
                }}
              />
            </div>
            <select
              className={selectCls}
              value={filtrosSes.cloud}
              onChange={e => {
                setOffsetSesiones(0);
                setFiltrosSes(f => ({ ...f, cloud: e.target.value }));
              }}
            >
              <option value="">Todas las nubes</option>
              {CLOUDS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              className={selectCls}
              value={filtrosSes.clasificacion_norm}
              onChange={e => {
                setOffsetSesiones(0);
                setFiltrosSes(f => ({ ...f, clasificacion_norm: e.target.value }));
              }}
            >
              <option value="">Todas las clasificaciones</option>
              {CLASIFICACION_OPTIONS.map(c => (
                <option key={c} value={c}>{CLASIFICACION_BADGE[c].label}</option>
              ))}
            </select>
            <button
              onClick={cargarSesiones}
              className="p-2 rounded-lg border border-white/10 text-white/50 hover:text-white transition-colors"
              title="Refrescar"
            >
              <RefreshCw size={14} className={loadingSesiones ? 'animate-spin' : ''} />
            </button>
            {canCreate && (
              <button
                onClick={() => setModalSes({ mode: 'create' })}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
                style={BTN_PRIMARY}
              >
                <Plus size={14} /> Nueva
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-white/40 border-b border-white/10">
                <th className="py-2 pr-3 font-medium">Nombre</th>
                <th className="py-2 px-3 font-medium">Cloud</th>
                <th className="py-2 px-3 font-medium">Clasificación</th>
                <th className="py-2 px-3 font-medium">Fecha</th>
                <th className="py-2 px-3 font-medium">Duración</th>
                <th className="py-2 px-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loadingSesiones && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-white/40">
                    <Loader2 size={18} className="animate-spin inline mr-2" /> Cargando sesiones…
                  </td>
                </tr>
              )}

              {!loadingSesiones && sesiones.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-white/40">
                    No hay sesiones con los filtros seleccionados.
                  </td>
                </tr>
              )}

              {!loadingSesiones && sesionesPagina.map(s => (
                <tr key={s.sesion_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{s.nombre}</span>
                      <button
                        onClick={() => setComentariosDe({ id: s.sesion_id, nombre: s.nombre, origen: 'sesion' })}
                        className="flex items-center gap-1 text-[11px] text-white/40 hover:text-sky-300 transition-colors"
                        title="Ver comentarios"
                      >
                        <MessageSquare size={12} />
                        {s.num_comentarios ?? 0}
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-3"><Badge value={s.cloud} map={CLOUD_BADGE} /></td>
                  <td className="py-3 px-3">
                    <Badge value={s.clasificacion_norm} map={CLASIFICACION_BADGE} />
                  </td>
                  <td className="py-3 px-3 text-white/60 text-xs">
                    {s.es_dia_completo === false && s.inicio_ts
                      ? fmtFechaHora(s.inicio_ts)
                      : fmtFecha(s.fecha_inicio)}
                  </td>
                  <td className="py-3 px-3 text-white/60 text-xs">
                    {s.duracion_horas != null ? `${Number(s.duracion_horas)} h` : 'Día completo'}
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setComentariosDe({ id: s.sesion_id, nombre: s.nombre, origen: 'sesion' })}
                        className="p-1.5 rounded-lg text-white/40 hover:text-sky-300 transition-colors"
                        title="Comentarios"
                      >
                        <MessageSquare size={14} />
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => setModalSes({ mode: 'edit', sesion: s })}
                          className="p-1.5 rounded-lg text-white/40 hover:text-sky-300 transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => setBorrarSes(s)}
                          className="p-1.5 rounded-lg text-white/40 hover:text-red-400 transition-colors"
                          title="Enviar a papelera"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loadingSesiones && totalPaginasSesiones > 1 && (
          <div className="flex items-center justify-between mt-4 text-xs text-white/50">
            <span>Página {paginaSesiones} de {totalPaginasSesiones} · {sesiones.length} sesiones</span>
            <div className="flex gap-2">
              <button
                disabled={offsetSesiones === 0}
                onClick={() => setOffsetSesiones(offset => Math.max(0, offset - PAGE_SIZE))}
                className="px-3 py-1.5 rounded-lg border border-white/10 disabled:opacity-30 hover:text-white transition-colors"
              >
                Anterior
              </button>
              <button
                disabled={paginaSesiones >= totalPaginasSesiones}
                onClick={() => setOffsetSesiones(offset => offset + PAGE_SIZE)}
                className="px-3 py-1.5 rounded-lg border border-white/10 disabled:opacity-30 hover:text-white transition-colors"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Calendario ───────────────────────────────────────────────────── */}
      <section className={`${CARD} p-5 alianzas-calendar`}>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CalendarDays size={18} style={{ color: ACCENT }} />
          Calendario mensual de sesiones
        </h2>

        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin]}
          initialView="dayGridMonth"
          locale={esLocale}
          height="auto"
          events={eventos}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek',
          }}
          datesSet={(info) => {
            const desde = info.startStr.slice(0, 10);
            const hasta = info.endStr.slice(0, 10);
            setRangoCal(prev =>
              prev && prev.desde === desde && prev.hasta === hasta ? prev : { desde, hasta }
            );
          }}
          eventClick={(info) => {
            const sesion = sesiones.find(s => s.sesion_id === info.event.id);
            if (sesion && canEdit) setModalSes({ mode: 'edit', sesion });
          }}
        />

        <style>{`
          .alianzas-calendar .fc {
            color: rgba(255,255,255,0.75);
            --fc-page-bg-color: transparent;
            --fc-neutral-bg-color: rgba(255,255,255,0.04);
            --fc-border-color: rgba(255,255,255,0.08);
            --fc-today-bg-color: ${tint(0.08)};
          }
          .alianzas-calendar .fc-theme-standard td,
          .alianzas-calendar .fc-theme-standard th,
          .alianzas-calendar .fc-scrollgrid { border-color: rgba(255,255,255,0.08) !important; }
          .alianzas-calendar .fc-col-header-cell {
            background: rgba(255,255,255,0.05);
            padding: 6px 0;
          }
          .alianzas-calendar .fc-col-header-cell-cushion {
            color: rgba(255,255,255,0.7);
            text-transform: capitalize;
            font-weight: 600;
            text-decoration: none;
          }
          .alianzas-calendar .fc-toolbar-title { font-size: 1rem; color: #fff; text-transform: capitalize; }
          .alianzas-calendar .fc-button {
            background: ${tint(0.15)} !important;
            border: 1px solid ${BORDER} !important;
            color: ${ACCENT} !important;
            text-transform: capitalize;
            box-shadow: none !important;
          }
          .alianzas-calendar .fc-button:disabled { opacity: 0.4; }
          .alianzas-calendar .fc-daygrid-day-number {
            color: rgba(255,255,255,0.6);
            text-decoration: none;
          }
          .alianzas-calendar .fc-day-other .fc-daygrid-day-number { color: rgba(255,255,255,0.25); }
          .alianzas-calendar .fc-event { cursor: pointer; font-size: 11px; }
        `}</style>
      </section>

      {/* ── Overlays ─────────────────────────────────────────────────────── */}
      {modalAsig && (
        <AsignacionModal
          mode={modalAsig.mode}
          asignacion={modalAsig.asignacion}
          usuarios={usuarios}
          onClose={() => setModalAsig(null)}
          onSave={guardarAsignacion}
        />
      )}

      {modalSes && (
        <SesionModal
          mode={modalSes.mode}
          sesion={modalSes.sesion}
          onClose={() => setModalSes(null)}
          onSave={guardarSesion}
        />
      )}

      {comentariosDe && (
        <ComentariosPanel
          item={comentariosDe}
          canWrite={canComment}
          catalogo={usuariosPorId}
          onClose={() => setComentariosDe(null)}
          onChanged={comentariosDe.origen === 'sesion' ? cargarSesiones : cargarAsignaciones}
          showToast={showToast}
        />
      )}

      {borrarAsig && (
        <ConfirmDialog
          title="Enviar a papelera"
          message={`¿Confirmas enviar a papelera "${borrarAsig.nombre}"?`}
          confirmLabel={borrandoAsig ? 'Eliminando…' : 'Eliminar'}
          busy={borrandoAsig}
          onClose={() => setBorrarAsig(null)}
          onConfirm={eliminarAsignacion}
        />
      )}

      {borrarSes && (
        <ConfirmDialog
          title="Enviar a papelera"
          message={`¿Confirmas enviar a papelera "${borrarSes.nombre}"?`}
          confirmLabel={borrandoSes ? 'Eliminando…' : 'Eliminar'}
          busy={borrandoSes}
          onClose={() => setBorrarSes(null)}
          onConfirm={eliminarSesion}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
